import { ArrowRight } from "lucide-react";
import { useCountAnimation } from "@/hooks/useCountAnimation";
import { useEffect, useState } from "react";
import type { HeroStats } from "@/types/domain";

interface HeroSectionProps {
  stats: HeroStats;
}

export const HeroSection = ({ stats }: HeroSectionProps) => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const statsLoaded = stats.finishers > 0 || stats.events > 0 || stats.cities > 0;
  const shouldAnimate = statsLoaded && !prefersReducedMotion;
  const animatedFinishers = useCountAnimation({ end: stats.finishers, duration: 2000, enabled: shouldAnimate });
  const animatedEvents = useCountAnimation({ end: stats.events, duration: 2000, enabled: shouldAnimate });
  const animatedCities = useCountAnimation({ end: stats.cities, duration: 2000, enabled: shouldAnimate });

  return (
    <section className="relative overflow-hidden -mt-16">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(260,75%,22%)] via-[hsl(235,75%,30%)] to-[hsl(210,80%,25%)]" />

      {/* Warm accent blobs */}
      <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-[hsl(var(--brand-orange)/0.12)] rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[hsl(280,85%,55%,0.1)] rounded-full blur-[100px]" />

      {/* Photo background — subtle, behind everything */}
      <div className="absolute inset-0 z-[1]">
        <img
          src="/images/events/summer-starting-line-sm.webp"
          alt="Quizrun runners in London"
          loading="lazy"
          className="w-full h-full object-cover opacity-[0.15]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(260,75%,22%,0.9)] via-[hsl(235,75%,30%,0.7)] to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-[1fr_480px] gap-6 lg:gap-12 items-center pt-24 pb-20 md:pt-32 md:pb-44 lg:pt-36 lg:pb-48">
          {/* Left: headline + CTA */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 mb-5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--brand-orange))] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[hsl(var(--brand-orange))]" />
              </span>
              <span className="text-xs tracking-wide text-white/80 font-medium">
                London · Berlin · Limassol · New York · Tbilisi · Haifa · Anywhere
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-black tracking-tight leading-[0.92]" style={{ fontSize: "clamp(3.25rem, 9vw, 6.75rem)" }}>
              <span className="text-white">Run</span>
              <br />
              <span className="text-[hsl(30_90%_65%)]">&amp; Think</span>
            </h1>

            <p className="mt-5 text-base md:text-lg text-white/85 max-w-md leading-relaxed text-pretty">
              A 5K where every correct quiz answer cuts your finish time. Outsmart the fast, outrun the smart.
            </p>

            {/* First-timer free card — newcomer reassurance sits right above the
                CTA so the shared Register link never reads as "will I be charged?" */}
            <div className="mt-6 inline-flex items-center gap-2.5 rounded-2xl px-4 py-3 bg-white/12 border border-[hsl(var(--brand-orange)/0.55)] backdrop-blur-sm shadow-lg shadow-black/20">
              <span className="text-[hsl(30_90%_65%)] text-lg leading-none">✦</span>
              <span className="text-sm md:text-[15px] font-bold text-white">
                Your first regular event's on us.
              </span>
              <span className="text-[10px] font-extrabold uppercase tracking-wider bg-brand-orange text-background rounded-full px-2 py-0.5">
                Free
              </span>
            </div>

            {/* CTAs */}
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
              <a
                href="#next-event"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-bold tracking-wide bg-brand-orange text-background hover:bg-[hsl(30_90%_50%)] transition-all hover:-translate-y-0.5 active:scale-[0.97] shadow-lg shadow-orange-500/25"
              >
                Join next event
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-1.5 py-2 text-sm font-medium text-white/75 hover:text-white transition-colors underline underline-offset-4 decoration-white/25 hover:decoration-white/60"
              >
                How does it work?
              </a>
            </div>

            {/* Stats bar — order: finishers (big number) → cities (reach) → events (cadence) */}
            <div className="mt-8 md:mt-12 flex flex-wrap gap-x-8 gap-y-3 sm:gap-x-10">
              <div>
                <div className="text-2xl md:text-3xl font-black tabular-nums text-white">{statsLoaded ? animatedFinishers : "500+"}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/65 mt-0.5">finishers</div>
              </div>
              <div className="border-l border-white/15 pl-8 sm:pl-10">
                <div className="text-2xl md:text-3xl font-black tabular-nums text-white">{statsLoaded ? animatedCities : "50+"}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/65 mt-0.5">cities</div>
              </div>
              <div className="border-l border-white/15 pl-8 sm:pl-10">
                <div className="text-2xl md:text-3xl font-black tabular-nums text-white">{statsLoaded ? animatedEvents : "50+"}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/65 mt-0.5">onsite events</div>
              </div>
            </div>
          </div>

          {/* Right: photo collage — visible on all screens */}
          <div className="relative">
            {/* Main photo */}
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10 lg:rotate-1">
              <img
                src="/images/events/autumn-off-they-go-sm.webp"
                alt="Off they go! — London, Sep 2025"
                fetchPriority="high"
                className="w-full h-auto object-cover aspect-[16/10] lg:aspect-[4/3]"
              />
            </div>

            {/* Overlapping smaller photo — visible md+. Sits inside the hero
                (no negative bottom offset that would clip into the wave). */}
            <div className="absolute bottom-2 -left-4 md:bottom-3 md:-left-6 w-32 md:w-44 rounded-xl overflow-hidden shadow-2xl border-2 border-white/20 -rotate-3 hidden md:block">
              <img
                src="/images/events/summer-park-group-sm.webp"
                alt="Quiz time — London, Jul 2025"
                loading="lazy"
                className="w-full h-auto object-cover aspect-[4/3]"
              />
            </div>

            {/* Floating stat card */}
            {/* a11y: bg-card adapts so text-brand-orange passes on the dark surface */}
            <div className="absolute -top-3 -right-3 bg-card rounded-xl shadow-2xl px-3 py-2.5 md:px-4 md:py-3 rotate-2 hidden sm:block">
              <div className="text-[10px] md:text-xs text-muted-foreground font-medium">Scoring example</div>
              <div className="text-xs md:text-sm font-bold text-foreground mt-0.5">32:00 − 5×2:30 = <span className="text-brand-orange">19:30</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave — offset -1px to eliminate subpixel gap */}
      <div className="absolute -bottom-px left-0 right-0 z-10">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none">
          <path d="M0 60V20C240 0 480 40 720 30C960 20 1200 0 1440 20V60H0Z" fill="hsl(var(--background))" />
        </svg>
      </div>
    </section>
  );
};
